import { extractApiError } from './api';

/**
 * **서버가 말한 거부 사유를 사용자 문구로** — axios 영문 raw는 한 겹 막는다.
 *
 * ## 왜 `extractApiError`를 그대로 쓰지 않나
 * 그 함수는 응답 본문에 `error`가 없으면 `NETWORK_ERROR` + **axios의 영문 message**
 * (`Request failed with status code 502`·`Network Error`)를 돌려준다. 그 문구가 토스트에 뜨면
 * 사용자는 무엇을 고쳐야 할지 알 수 없으므로 **그 갈래만** 호출부 문구로 떨어뜨린다.
 * 즉 이 함수가 더하는 것은 「서버 문구를 쓴다」가 아니라 **「서버 문구가 없을 때 영문이 새지 않는다」**다.
 *
 * ## 왜 `error.message`를 그대로 띄워도 되나 (2026-10-27 확인)
 * 서버 `http-exception.filter.ts`가 **예상치 못한 `Error`의 원본 message·stack을 `details`로만**
 * 보내고(그조차 `NODE_ENV === 'development'`에서만) `message`는 고정 한글
 * (`서버 오류가 발생했습니다.`)로 남긴다. 이 함수는 `details`를 읽지 않으므로 stack·내부
 * 식별자가 사용자 문구로 새는 경로가 없다. 남는 것은 class-validator의 첫 문구(영문 필드명)뿐이고
 * admin은 슈퍼관리자 전용이라 수용한다.
 *
 * ## ⛔ web·mobile로 옮길 때는 그 수용 판단을 «다시» 해야 한다
 * 위 수용의 근거는 「이 문구를 보는 사람이 **슈퍼관리자뿐**」이라는 것이고 **web은 그렇지 않다**.
 * 서버가 `message`에 싣는 것 중 사람이 고른 문구가 아닌 갈래가 하나 있다 — class-validator의
 * **첫 문구**(`http-exception.filter.ts`가 `resp.message[0]`을 쓴다 · code는
 * `VALIDATION_INVALID_INPUT`)이고 그것은 **영문 필드명**(`widgetTree should not be empty`)이다.
 * 고객에게 그것을 띄우면 무엇을 고칠지 알 수 없고 내부 필드명까지 드러난다.
 * ⇒ web으로 가져가려면 `VALIDATION_INVALID_INPUT`도 폴백 갈래에 넣을지 **그 자리에서 판정**하라.
 * 이 함수가 지금 그것을 통과시키는 것은 **admin 한정 수용**이고 설계 불변식이 아니다.
 *
 * ## 같은 일을 하는 사본 둘 — 수렴 대상이다
 * `company-rbac.ts`의 `companyRbacErrorMessage`(+ shared 에러코드 기본 문구 한 겹이 더 있다)와
 * `story-monitor/StoryDetailModal.tsx`의 `apiMessage`(폴백이 인자가 아니라 고정 문구다). 셋이
 * 같은 가드를 각자 들고 있으니 **그중 하나가 또 낡는다** — 호출부를 넓힐 때 이 함수로 모을 것.
 * 그 둘을 지금 손대지 않은 것은 범위 밖이기 때문이고 「그 형태가 옳아서」가 아니다.
 *
 * ## ⛔ web 에 같은 이름이 둘 더 있고 «사본이 아니다» — 분간표는 web 쪽이 정본이다
 * `apps/web/src/lib/api.ts`의 `apiErrorMessage` JSDoc에 세 자리 분간표가 있다(읽는 봉투 ·
 * 폴백 소유 · 반환형). 여기 베껴 적지 않는다 — 두 자리에 적으면 한쪽이 낡는다.
 * 그 표가 적는 한 줄만 옮긴다: **섞어 쓰는 것은 tsc가 막고(`fallback`이 required라 `TS2554`)
 * 그 집행이 이 매개변수의 required 하나에 매달려 있다** ⇒ ⛔ `fallback?: string`으로 바꾸지 말 것.
 *
 * @param fallback 서버가 사유를 주지 않았을 때 쓸 문구. 호출부가 **이미 쓰던 문구**를 그대로 넘기면
 *   동작이 넓어지기만 하고 좁아지지 않는다.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const { code, message } = extractApiError(error);
  /**
   * ⚠️ **방어적 코드가 아니다 — 지우면 axios 영문이 사용자 문구가 된다.**
   * 2026-10-27 프로브 실측: 이 한 줄만 지우면 `__tests__/apiErrorMessage.test.ts`의
   * `[대조군-가드]` 둘이 red가 되고 그 메시지가 `'Network Error'`·
   * `'알 수 없는 오류가 발생했습니다.'`가 새어 나온 것을 그대로 보여 준다(그 파일 헤더의 프로브 표).
   */
  if (code === 'NETWORK_ERROR' || code === 'UNKNOWN_ERROR') return fallback;
  // 서버가 `error`를 실었어도 message가 빈 문자열일 수 있다 — 빈 토스트보다 호출부 문구가 낫다.
  return message.trim() ? message : fallback;
}
